import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useRouter } from 'next/router';

interface UserAvatarProps {
    className?: string;
    onLogout?: () => void;
    onUpgrade?: () => void;
}

export function UserAvatar({
    className = '',
    onLogout: onLogoutCallback,
    onUpgrade,
}: UserAvatarProps) {
    const { user, logout, getProfileImageUrl, isPaidPlan } = useUser();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const router = useRouter();

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (!user) {
        return null;
    }

    const handleLogout = () => {
        logout(); // This clears the user data from context and localStorage
        setIsDropdownOpen(false);

        // Call the parent callback to reset to step 0 if provided
        if (onLogoutCallback) {
            onLogoutCallback();
        } else {
            // Fallback to page refresh if no callback provided
            window.location.href = '/';
        }
    };

    const profileImageUrl = getProfileImageUrl();

    // Use shared isPaidPlan() from context for DRY
    const hasPaidPlan = isPaidPlan();

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div className="flex items-center gap-2">
                {/* Avatar Button */}
                <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onBlur={() => {
                        // Delay to allow clicking on dropdown items
                        setTimeout(() => setIsDropdownOpen(false), 200);
                    }}
                    className={`relative w-10 h-10 rounded-full overflow-hidden border-2 transition-colors duration-200 focus:outline-none flex items-center justify-center ${
                        !user.profile_path ? 'bg-gray-800' : ''
                    } ${
                        isDropdownOpen
                            ? 'border-purple-500 ring-2 ring-purple-500 ring-offset-2 ring-offset-gray-900'
                            : 'border-gray-600 hover:border-purple-500'
                    }`}
                    aria-label="User menu"
                >
                    {/* Show icon if using default profile, otherwise show image */}
                    {!user.profile_path ? (
                        <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                        </svg>
                    ) : (
                        <img
                            src={profileImageUrl}
                            alt={user.name || user.email}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                // If custom image fails to load, show icon instead
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                            }}
                        />
                    )}
                </button>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-700">
                        <p className="text-sm text-gray-400">Signed in as</p>
                        <p className="text-sm font-medium text-white truncate">
                            {user.email}
                        </p>
                        {user.name && (
                            <p className="text-sm text-gray-300 truncate mt-1">
                                {user.name}
                            </p>
                        )}
                        {/* Plan Badge in dropdown - always show, default to "Free" if not set */}
                        <div className="mt-2">
                            <span
                                className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                                    hasPaidPlan
                                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                                        : 'bg-gray-700 text-gray-300'
                                }`}
                            >
                                {user.plan_display_name || 'Free'} Plan
                            </span>
                        </div>
                    </div>

                    <div className="py-1">
                        {/* Upgrade option - shown when user's plan is upgradable (default true for free users) */}
                        {user.plan_upgradable !== false &&
                            !hasPaidPlan &&
                            onUpgrade && (
                                <button
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        onUpgrade();
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-purple-400 hover:bg-purple-900/30 hover:text-purple-300 transition-colors duration-150"
                                >
                                    <span className="flex items-center">
                                        {/* Diamond gem icon */}
                                        <svg
                                            className="w-4 h-4 mr-2"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M6 2L1 9l11 13L23 9l-5-7H6zm1.4 1h9.2l3.6 5H4.8l2.6-5zM12 19.5L3.5 10h17L12 19.5z" />
                                        </svg>
                                        Upgrade Plan
                                    </span>
                                </button>
                            )}

                        <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-150"
                        >
                            <span className="flex items-center">
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                    />
                                </svg>
                                Logout
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
